import { apiSlice } from './apiSlice';

/* ================= TYPES ================= */

export interface Competition {
  id: number;
  externalId: string;
  providerId: number | null;
  name: string;
  sport: string;
  startDate: string | null;
  endDate: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitionsResponse {
  success: boolean;
  message: string;
  competitions: Competition[];
}

export interface CompetitionEvent {
  id: string;
  name: string;
}

export interface CompetitionEventResponse {
  success: boolean;
  data: CompetitionEvent[];
}

export interface Market {
  id: string;
  name: string;
}

export interface MarketResponse {
  success: boolean;
  data: Market[];
}

export interface CompetitionsEventsMarketsApiResponse {
  success: boolean;
  message: string;
  data: {
    success: boolean;
    data: {
      [sport: string]: CompetitionItem[];
    };
  };
}

export interface CompetitionItem {
  id: number;
  name: string;
  sport: string;
  events: {
    id: string;
    name: string;
    startTime: string;
    markets: {
      id: string;
      name: string;
    }[];
  }[];
}

/* ================= API ================= */

export const competitionApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ✅ 1. Get Competitions
    getCompetitions: builder.query<CompetitionsResponse, { sport: string; search?: string }>({
      query: ({ sport, search }) => ({
        url: `/competitions`,
        method: 'GET',
        params: {
          sport: sport === 'Football' ? 'Soccer' : sport,
          search,
        },
      }),
      providesTags: ['Sports'],
    }),

    // ✅ 2. Get Events by Competition
    getCompetitionEvents: builder.query<CompetitionEventResponse, number>({
      query: (competitionId) => ({
        url: `/competitions/event/${competitionId}`,
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),

    // ✅ 3. Get Markets by Event
    getEventMarkets: builder.query<MarketResponse, number | string>({
      query: (eventId) => ({
        url: `/competitions/market/${eventId}`,
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),

    getEventCount: builder.query<
      {
        success: boolean;
        message: string;
        count: {
          total: number;
          inplay: number;
          favorite: number;
        };
      },
      { isAuthenticated: boolean }
    >({
      query: ({ isAuthenticated }) => ({
        url: `/fixture/eventcount${isAuthenticated ? '/user' : ''}`,
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),

    getCompetitionsEventsMarkets: builder.query<
      CompetitionsEventsMarketsApiResponse,
      { eventSearch?: string; competitionSearch?: string; sport?: string }
    >({
      query: ({ eventSearch, sport, competitionSearch }) => ({
        url: `/competitions/event/market`,
        params: { eventSearch, sport, competitionSearch },
        method: 'GET',
      }),
      providesTags: ['Sports'],
    }),
  }),
  overrideExisting: false,
});

/* ================= HOOKS ================= */

export const {
  useGetCompetitionsQuery,
  useLazyGetCompetitionsQuery,

  useGetCompetitionEventsQuery,
  useLazyGetCompetitionEventsQuery,

  useGetEventMarketsQuery,
  useLazyGetEventMarketsQuery,
  useGetEventCountQuery,

  useGetCompetitionsEventsMarketsQuery,
} = competitionApi;
